import { FC, useEffect, useMemo, useState } from "react"
import { Dialog } from "@headlessui/react"
import {
  X,
  Calculator,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Info,
  Loader2
} from "lucide-react"
import {
  BillingConfigDetails,
  EstimateBillingRequest,
  EstimateBillingResponse,
  EstimateBillingLevel,
  EstimateTieredComponent,
  EstimateUsage
} from "../../../../types"
import {
  useEstimateBillingQuery,
  useFetchBillingPlansQuery,
  useFetchOrgMonthlyUsageQuery
} from "../../../../redux/queries"
import { formatNumber, numberToUSD } from "../../../utils/numeric"

interface Props {
  open: boolean
  onClose: () => void
  orgId: string
  orgName: string
  details: BillingConfigDetails
  savedDetails: BillingConfigDetails | null
}

const DEBOUNCE_MS = 350

const EMPTY_USAGE: EstimateUsage = {
  leads: 0,
  emails: 0,
  sms_threads: 0,
  pending_subscriptions: 0,
  api_calls: 0,
  ai_chat_tokens: 0,
  ai_advisor_tokens: 0,
  lead_scoring_tokens: 0
}

// Only the fields that materially affect the estimate.
const estimateDetails = (d: BillingConfigDetails) => ({
  level: d.level as EstimateBillingLevel,
  features: d.features ?? [],
  addons: d.addons ?? []
})

const buildArgs = (
  d: BillingConfigDetails,
  usage: EstimateUsage
): EstimateBillingRequest => ({
  details: estimateDetails(d),
  usage
})

const sameEstimateInputs = (a: BillingConfigDetails, b: BillingConfigDetails) =>
  JSON.stringify(estimateDetails(a)) === JSON.stringify(estimateDetails(b))

interface UsageFieldDef {
  key: keyof EstimateUsage
  label: string
  helper?: string
  placeholder?: string
}

const USAGE_FIELDS: UsageFieldDef[] = [
  { key: "leads", label: "Leads" },
  { key: "emails", label: "Emails" },
  { key: "sms_threads", label: "SMS threads" },
  { key: "pending_subscriptions", label: "Pending subs" },
  { key: "api_calls", label: "API calls" },
  {
    key: "ai_chat_tokens",
    label: "AI chat tokens",
    helper: "Raw tokens (e.g. 2,000,000)"
  },
  {
    key: "ai_advisor_tokens",
    label: "AI advisor tokens",
    helper: "Raw tokens"
  },
  {
    key: "lead_scoring_tokens",
    label: "Lead scoring tokens",
    helper: "Raw tokens"
  }
]

const PreviewBillModal: FC<Props> = ({
  open,
  onClose,
  orgId,
  orgName,
  details,
  savedDetails
}) => {
  const { data: plansData, isLoading: plansLoading } =
    useFetchBillingPlansQuery()

  // Last-month actual usage — only fetched once the modal opens.
  const {
    data: lastMonthData,
    isFetching: isFetchingLastMonth,
    error: lastMonthError
  } = useFetchOrgMonthlyUsageQuery({ org_id: orgId }, { skip: !open || !orgId })

  // Compute a plan's included amounts for a given feature set. Uses
  // the fetched plans data when available; falls back to 0. Fields
  // behind feature gates (AI tokens, pending subs, api calls) are
  // only populated when the matching feature is active — otherwise
  // they're ignored by the estimator anyway.
  const includedUsageFor = (
    level: string,
    featureList: string[]
  ): EstimateUsage => {
    const plan = plansData?.plans?.[level]
    const overage = plansData?.overage ?? {}
    // included_api_calls may not be typed on BillingPlanDetails; read loosely.
    const includedApiCalls = (
      plan as { included_api_calls?: number } | undefined
    )?.included_api_calls

    const aiKey = (primary: string, fallback?: string) => {
      const m = overage[primary] ?? (fallback ? overage[fallback] : undefined)
      return m?.included_tokens ?? 0
    }

    const hasPendingSubsFeature =
      featureList.includes("pending_subscriptions") ||
      featureList.includes("dream_rate")
    const hasApiAccessFeature =
      level === "api" || featureList.includes("api_access")

    return {
      leads: plan?.included_leads ?? 0,
      emails: plan?.included_emails ?? 0,
      sms_threads: plan?.included_sms_threads ?? 0,
      pending_subscriptions: hasPendingSubsFeature
        ? overage.pending_subscriptions?.included_tokens ?? 0
        : 0,
      api_calls: hasApiAccessFeature
        ? includedApiCalls ??
          overage.api_calls?.included_tokens ??
          (level === "api" ? 250000 : 0)
        : 0,
      ai_chat_tokens: featureList.includes("ai_website_chat")
        ? aiKey("ai_chat_tokens", "ai_website_chat")
        : 0,
      ai_advisor_tokens: featureList.includes("ai_advisor")
        ? aiKey("ai_advisor_tokens", "ai_advisor")
        : 0,
      lead_scoring_tokens: featureList.includes("lead_scoring_ai")
        ? aiKey("lead_scoring_tokens", "lead_scoring_ai")
        : 0
    }
  }

  const [usage, setUsage] = useState<EstimateUsage>(EMPTY_USAGE)

  // Whether the user has taken any action on usage (typed a value or
  // clicked a preset). Once true, we stop auto-applying last-month
  // data so their work isn't overwritten.
  const [usageTouched, setUsageTouched] = useState(false)

  // When the modal opens, seed usage with the plan's included amounts
  // as a placeholder. Last-month actual usage (if it arrives) will
  // replace this via the effect below.
  useEffect(() => {
    if (!open) {
      setUsageTouched(false)
      return
    }
    setUsage(includedUsageFor(details.level, details.features ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, details.level, details.features, plansData])

  // Auto-apply last-month usage as the default once it arrives — but
  // only if the user hasn't started editing or picked a different
  // preset yet.
  useEffect(() => {
    if (!open || usageTouched || !lastMonthData?.usage) return
    setUsage({ ...EMPTY_USAGE, ...lastMonthData.usage })
  }, [open, usageTouched, lastMonthData])

  // Debounce details + usage. Live edits to the underlying form trickle
  // in via the details prop; debouncing avoids firing an estimate on
  // every keystroke.
  const [debouncedDetails, setDebouncedDetails] =
    useState<BillingConfigDetails>(details)
  const [debouncedUsage, setDebouncedUsage] =
    useState<EstimateUsage>(EMPTY_USAGE)

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedDetails(details)
      setDebouncedUsage(usage)
    }, DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [details, usage])

  const currentArgs = useMemo(
    () => buildArgs(debouncedDetails, debouncedUsage),
    [debouncedDetails, debouncedUsage]
  )

  const skipSaved =
    !savedDetails || sameEstimateInputs(debouncedDetails, savedDetails)
  const savedArgs = useMemo(
    () => (savedDetails ? buildArgs(savedDetails, debouncedUsage) : null),
    [savedDetails, debouncedUsage]
  )

  const {
    data: currentEstimate,
    error: currentError,
    isFetching: isFetchingCurrent
  } = useEstimateBillingQuery(currentArgs, { skip: !open })

  const { data: savedEstimate } = useEstimateBillingQuery(
    (savedArgs ?? currentArgs) as EstimateBillingRequest,
    { skip: !open || skipSaved || !savedArgs }
  )

  const delta =
    !skipSaved && currentEstimate && savedEstimate
      ? currentEstimate.total - savedEstimate.total
      : null

  const handleSetPreset = (
    mode: "last_month" | "included" | "double" | "clear"
  ) => {
    setUsageTouched(true)
    if (mode === "last_month") {
      if (lastMonthData?.usage) {
        setUsage({ ...EMPTY_USAGE, ...lastMonthData.usage })
      }
      return
    }
    if (mode === "clear") {
      setUsage(EMPTY_USAGE)
      return
    }
    const included = includedUsageFor(details.level, details.features ?? [])
    if (mode === "included") {
      setUsage(included)
      return
    }
    // double
    setUsage({
      leads: (included.leads ?? 0) * 2,
      emails: (included.emails ?? 0) * 2,
      sms_threads: (included.sms_threads ?? 0) * 2,
      pending_subscriptions: 0,
      api_calls: (included.api_calls ?? 0) * 2,
      ai_chat_tokens: 0,
      ai_advisor_tokens: 0,
      lead_scoring_tokens: 0
    })
  }

  const updateUsage = (key: keyof EstimateUsage, raw: string) => {
    const parsed = raw === "" ? 0 : parseInt(raw, 10)
    setUsageTouched(true)
    setUsage(prev => ({
      ...prev,
      [key]: Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
    }))
  }

  const errorMessage = (() => {
    if (!currentError) return null
    const err = currentError as { status?: number; error?: string; data?: any }
    if (err?.status === 403)
      return "Not authorized to run billing estimates. Administrator access required."
    if (err?.data?.message) return String(err.data.message)
    if (err?.error) return err.error
    return "Estimate unavailable."
  })()

  // BillingConfigDetails doesn't currently include "api" as a level,
  // but the estimator supports it — handle defensively via string compare.
  const isCustom = details.level === "custom"
  const isApi = (details.level as string) === "api"

  // Feature gating for usage inputs. The backend ignores these fields
  // unless the matching feature is enabled (or the plan has built-in
  // support, e.g. api_calls on the api plan). We grey out inputs that
  // wouldn't affect the bill so admins don't waste time on them.
  const features = details.features ?? []
  const hasPendingSubs =
    features.includes("pending_subscriptions") ||
    features.includes("dream_rate")
  const hasApiAccess = isApi || features.includes("api_access")
  const usageDisabled: Record<keyof EstimateUsage, boolean> = {
    leads: isCustom || isApi,
    emails: isCustom || isApi,
    sms_threads: isCustom || isApi,
    pending_subscriptions: isCustom || isApi || !hasPendingSubs,
    // api_calls bill on the api plan OR whenever api_access is enabled
    api_calls: isCustom || !hasApiAccess,
    ai_chat_tokens: isCustom || isApi || !features.includes("ai_website_chat"),
    ai_advisor_tokens: isCustom || isApi || !features.includes("ai_advisor"),
    lead_scoring_tokens:
      isCustom || isApi || !features.includes("lead_scoring_ai")
  }

  const disabledReason = (key: keyof EstimateUsage): string | null => {
    if (isCustom) return "Custom plans ignore usage"
    if (key === "api_calls" && !hasApiAccess)
      return "Enable API Access to bill for this"
    if (isApi && key !== "api_calls") return "API plans ignore this usage"
    if (key === "pending_subscriptions" && !hasPendingSubs)
      return "Enable Dream Rate to bill for this"
    if (key === "ai_chat_tokens" && !features.includes("ai_website_chat"))
      return "Enable AI Website Chat to bill for this"
    if (key === "ai_advisor_tokens" && !features.includes("ai_advisor"))
      return "Enable AI Advisor to bill for this"
    if (key === "lead_scoring_tokens" && !features.includes("lead_scoring_ai"))
      return "Enable Lead Scoring AI to bill for this"
    return null
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Calculator size={18} className="text-gray-500" />
              <Dialog.Title className="text-base font-semibold text-gray-900">
                Preview Bill
                {orgName && (
                  <span className="text-gray-500 font-normal">
                    {" "}
                    — {orgName}
                  </span>
                )}
              </Dialog.Title>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {/* Hero */}
            <div className="px-6 py-6 text-center border-b border-gray-100">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                Estimated Monthly Bill
              </p>
              <div className="relative inline-block">
                <span className="text-4xl font-semibold text-gray-900">
                  {currentEstimate
                    ? numberToUSD(currentEstimate.total)
                    : errorMessage
                    ? "—"
                    : "…"}
                </span>
                {isFetchingCurrent && currentEstimate && (
                  <Loader2
                    size={16}
                    className="absolute -right-6 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"
                  />
                )}
              </div>
              {delta !== null && delta !== 0 && (
                <div
                  className={`mt-2 inline-flex items-center gap-1 text-sm font-medium ${
                    delta > 0 ? "text-amber-600" : "text-green-600"
                  }`}
                >
                  {delta > 0 ? (
                    <TrendingUp size={14} />
                  ) : (
                    <TrendingDown size={14} />
                  )}
                  {delta > 0 ? "+" : ""}
                  {numberToUSD(delta)}/mo vs. saved
                </div>
              )}
              {delta === 0 && savedDetails && (
                <p className="mt-2 text-xs text-gray-400">
                  No change vs. saved config
                </p>
              )}
            </div>

            {/* Error banner */}
            {errorMessage && (
              <div className="mx-6 mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle
                  size={16}
                  className="text-red-500 flex-shrink-0 mt-0.5"
                />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {/* Plan-mode info banners */}
            {isCustom && (
              <div className="mx-6 mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Info
                  size={16}
                  className="text-amber-500 flex-shrink-0 mt-0.5"
                />
                <p className="text-sm text-amber-700">
                  Custom plans bill base + add-ons only; usage values are
                  ignored.
                </p>
              </div>
            )}
            {isApi && (
              <div className="mx-6 mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Info
                  size={16}
                  className="text-amber-500 flex-shrink-0 mt-0.5"
                />
                <p className="text-sm text-amber-700">
                  API plans bill on API calls only; other usage values are
                  ignored.
                </p>
              </div>
            )}

            {/* Usage inputs */}
            <section className="px-6 py-5 border-b border-gray-100">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-700">Usage</h3>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleSetPreset("last_month")}
                    disabled={!lastMonthData?.usage || isFetchingLastMonth}
                    className="px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 transition-colors"
                    title={
                      lastMonthData
                        ? `Prefill with ${lastMonthData.month} ${lastMonthData.year} usage`
                        : "Last month usage"
                    }
                  >
                    Last month
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetPreset("included")}
                    disabled={plansLoading}
                    className="px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 transition-colors"
                  >
                    Included
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetPreset("double")}
                    disabled={plansLoading}
                    className="px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 transition-colors"
                  >
                    2× included
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetPreset("clear")}
                    className="px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <p className="mb-3 text-xs text-gray-400">
                {isFetchingLastMonth && "Loading last month's usage…"}
                {!isFetchingLastMonth &&
                  lastMonthData &&
                  `Prefilled from ${lastMonthData.month} ${lastMonthData.year} actuals.`}
                {!isFetchingLastMonth &&
                  !lastMonthData &&
                  lastMonthError &&
                  "Couldn't load last month's usage — showing plan's included amounts."}
                {!isFetchingLastMonth &&
                  !lastMonthData &&
                  !lastMonthError &&
                  "Showing plan's included amounts."}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                {USAGE_FIELDS.map(field => {
                  const disabled = usageDisabled[field.key]
                  const reason = disabled ? disabledReason(field.key) : null
                  const helper = reason || field.helper
                  return (
                    <div key={field.key}>
                      <label
                        className={`block text-xs mb-1 ${
                          disabled ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {field.label}
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={usage[field.key] ?? 0}
                        onChange={e => updateUsage(field.key, e.target.value)}
                        disabled={disabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                      />
                      {helper && (
                        <p className="mt-1 text-[11px] text-gray-400">
                          {helper}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>

            {/* Breakdown */}
            <section className="px-6 py-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Breakdown
              </h3>
              {currentEstimate ? (
                <BreakdownList estimate={currentEstimate} />
              ) : (
                <p className="text-sm text-gray-400">
                  {errorMessage ? "—" : "Calculating…"}
                </p>
              )}
            </section>

            {/* Footer note */}
            <p className="px-6 pb-5 text-[11px] text-gray-400">
              Estimates are calculated server-side. Setup fees and taxes are not
              included.
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

// ---------- Breakdown rendering ----------

const BreakdownList: FC<{ estimate: EstimateBillingResponse }> = ({
  estimate
}) => {
  const b = estimate.breakdown
  return (
    <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
      <SimpleRow label="Base plan" amount={b.base} />
      {b.leads && (
        <TieredRow
          label="Leads"
          unit="leads"
          component={b.leads}
          defaultOpen={b.leads.total > 0}
        />
      )}
      {b.emails && (
        <TieredRow
          label="Emails"
          unit="emails"
          component={b.emails}
          defaultOpen={b.emails.total > 0}
        />
      )}
      {b.sms && (
        <TieredRow
          label="SMS"
          unit="threads"
          component={b.sms}
          defaultOpen={b.sms.total > 0}
        />
      )}
      {b.pending_subscriptions && (
        <TieredRow
          label="Pending subscriptions"
          unit="subs"
          component={b.pending_subscriptions}
          defaultOpen={b.pending_subscriptions.total > 0}
        />
      )}
      {b.api_calls && (
        <TieredRow
          label="API calls"
          unit="calls"
          component={b.api_calls}
          defaultOpen={b.api_calls.total > 0}
        />
      )}
      {b.ai_website_chat && (
        <TieredRow
          label="AI Website Chat"
          unit="tokens"
          component={b.ai_website_chat}
          defaultOpen={b.ai_website_chat.total > 0}
        />
      )}
      {b.ai_advisor && (
        <TieredRow
          label="AI Advisor"
          unit="tokens"
          component={b.ai_advisor}
          defaultOpen={b.ai_advisor.total > 0}
        />
      )}
      {b.lead_scoring_ai && (
        <TieredRow
          label="Lead Scoring AI"
          unit="tokens"
          component={b.lead_scoring_ai}
          defaultOpen={b.lead_scoring_ai.total > 0}
        />
      )}
      {b.api_access && (
        <SimpleRow label="API Access" amount={b.api_access.total} />
      )}
      {b.addons && b.addons.breakdown.length > 0 && (
        <AddonsRow
          component={b.addons}
          defaultOpen={b.addons.breakdown.length > 0}
        />
      )}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
        <span className="text-sm font-semibold text-gray-900">Total</span>
        <span className="text-sm font-semibold text-gray-900">
          {numberToUSD(estimate.total)}
        </span>
      </div>
    </div>
  )
}

const SimpleRow: FC<{ label: string; amount: number }> = ({
  label,
  amount
}) => (
  <div className="flex items-center justify-between px-4 py-2.5">
    <span className="text-sm text-gray-700">{label}</span>
    <span className="text-sm text-gray-900 tabular-nums">
      {numberToUSD(amount)}
    </span>
  </div>
)

interface TieredRowProps {
  label: string
  unit: string
  component: EstimateTieredComponent
  defaultOpen?: boolean
}

const TieredRow: FC<TieredRowProps> = ({
  label,
  unit,
  component,
  defaultOpen = false
}) => {
  // The API returns `subtotal` only for tiers actually reached by the
  // current usage — filter to those so we don't render noise.
  const reachedTiers = (component.breakdown ?? []).filter(
    t => t.subtotal !== undefined
  )
  const hasBaseFee = !!component.base_fee && component.base_fee > 0
  const hasDetail = reachedTiers.length > 0 || hasBaseFee

  const [open, setOpen] = useState(defaultOpen)

  return (
    <div>
      <button
        type="button"
        onClick={() => hasDetail && setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-left ${
          hasDetail ? "hover:bg-gray-50" : "cursor-default"
        }`}
      >
        <span className="flex items-center gap-1.5 text-sm text-gray-700">
          {hasDetail &&
            (open ? (
              <ChevronDown size={14} className="text-gray-400" />
            ) : (
              <ChevronRight size={14} className="text-gray-400" />
            ))}
          {label}
        </span>
        <span className="text-sm text-gray-900 tabular-nums">
          {numberToUSD(component.total)}
        </span>
      </button>
      {open && hasDetail && (
        <div className="px-4 pb-3 -mt-1 space-y-1 text-xs text-gray-500">
          {hasBaseFee && (
            <div className="flex items-center justify-between pl-5">
              <span>Base fee</span>
              <span className="tabular-nums">
                {numberToUSD(component.base_fee as number)}
              </span>
            </div>
          )}
          {reachedTiers.map((tier, i) => (
            <div
              key={i}
              className="flex items-center justify-between pl-5 gap-3"
            >
              <span>
                {formatNumber(tier.start)}
                {tier.stop !== undefined
                  ? `–${formatNumber(tier.stop)}`
                  : "+"}{" "}
                {unit}
                {tierRate(tier)}
              </span>
              <span className="tabular-nums">
                {numberToUSD(tier.subtotal ?? 0, false)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const tierRate = (tier: {
  cpl?: number
  cpe?: number
  cpt?: number
  cpc?: number
  cpp?: number
}) => {
  const rate = tier.cpl ?? tier.cpe ?? tier.cpt ?? tier.cpc ?? tier.cpp
  if (rate === undefined) return ""
  // Show cents for sub-dollar rates (e.g. $0.20 per email)
  const formatted =
    rate < 1 ? `$${rate.toFixed(2)}` : numberToUSD(rate, rate % 1 === 0, 4)
  return ` @ ${formatted}`
}

interface AddonsRowProps {
  component: NonNullable<EstimateBillingResponse["breakdown"]["addons"]>
  defaultOpen?: boolean
}

const AddonsRow: FC<AddonsRowProps> = ({ component, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50"
      >
        <span className="flex items-center gap-1.5 text-sm text-gray-700">
          {open ? (
            <ChevronDown size={14} className="text-gray-400" />
          ) : (
            <ChevronRight size={14} className="text-gray-400" />
          )}
          Add-ons
        </span>
        <span className="text-sm text-gray-900 tabular-nums">
          {numberToUSD(component.total)}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 -mt-1 space-y-1 text-xs text-gray-500">
          {component.breakdown.map((addon, i) => {
            const isDiscount = addon.type === "discount"
            const primary = addon.label || addon.description || addon.type
            const secondary =
              addon.label &&
              addon.description &&
              addon.description !== addon.label
                ? addon.description
                : null
            return (
              <div
                key={i}
                className="flex items-start justify-between pl-5 gap-3"
              >
                <span>
                  <span className="text-gray-700">{primary}</span>
                  {secondary && (
                    <span className="ml-1 text-gray-400">· {secondary}</span>
                  )}
                  {isDiscount && " (discount)"}
                </span>
                <span
                  className={`tabular-nums ${
                    isDiscount ? "text-green-600" : "text-gray-700"
                  }`}
                >
                  {isDiscount ? "−" : ""}
                  {numberToUSD(addon.monthly_price)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default PreviewBillModal
